import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../contexts/ThemeContext'
import { fonts } from '../../lib/themes'
import { IcMail, IcLock, IcFlower, IcUser } from '../ui/Icons'

const CSS = `
@keyframes flDrift {
  0%,100% { transform:translateY(0) rotate(0); opacity:0.55; }
  50% { transform:translateY(-10px) rotate(18deg); opacity:1; }
}
@keyframes flGrow {
  from { transform:scaleY(0); }
  to { transform:scaleY(1); }
}
@keyframes flUnfurl {
  0% { transform:scale(0); opacity:0; }
  60% { opacity:1; }
  100% { transform:scale(1); opacity:1; }
}
@keyframes flPop {
  0%,70% { transform:scale(0); opacity:0; }
  100% { transform:scale(1); opacity:1; }
}
@keyframes flShine {
  0% { left:-80%; }
  100% { left:140%; }
}
`

function LeafSpark({ style }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" style={style}>
      <path d="M12 3c-5 0-9 5-9 9 5 0 9-5 9-9z" opacity="0.55"/>
      <path d="M12 3c5 0 9 5 9 9-5 0-9-5-9-9z"/>
    </svg>
  )
}

export default function Login() {
  const { signIn, signUp, resetPassword, updatePassword, isRecovery } = useAuth()
  const { theme } = useTheme()
  const s = makeStyles(theme)

  const [modo, setModo]       = useState('entrar')
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError]     = useState('')
  const [mensaje, setMensaje] = useState('')
  const [cargando, setCargando] = useState(false)
  const [shineKey, setShineKey] = useState(0)
  const [nombre, setNombre]   = useState('')
  const [genero, setGenero]   = useState('')
  const [fechaNac, setFechaNac] = useState('')

  useEffect(() => {
    if (isRecovery) {
      setModo('nueva_contrasena')
      setError('')
      setMensaje('')
    }
  }, [isRecovery])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setMensaje('')

    if (modo === 'recuperar') {
      setCargando(true)
      const { error } = await resetPassword(email)
      setCargando(false)
      if (error) setError(mensajeError(error.message))
      else setMensaje('te enviamos un enlace a tu correo — revísalo para continuar')
      return
    }

    if (modo === 'nueva_contrasena') {
      if (password !== confirmPassword) {
        setError('las contraseñas no coinciden')
        return
      }
      setCargando(true)
      const { error } = await updatePassword(password)
      setCargando(false)
      if (error) setError(mensajeError(error.message))
      else setMensaje('contraseña actualizada — entrando a tu jardín...')
      return
    }

    if (modo === 'crear' && !genero) {
      setError('elige cómo te identifies para continuar')
      return
    }
    setCargando(true)
    const fn = modo === 'entrar' ? signIn : signUp
    const { error } = await fn(email, password)
    setCargando(false)
    if (error) {
      setError(mensajeError(error.message))
    } else if (modo === 'crear') {
      localStorage.setItem('florece-pending-profile', JSON.stringify({ nombre, genero, fechaNac }))
      setMensaje('revisa tu correo para confirmar tu cuenta')
    }
  }

  function mensajeError(raw) {
    if (raw.includes('Invalid login')) return 'ese correo o contraseña no coinciden'
    if (raw.includes('already registered')) return 'ya tienes una cuenta, ¿quieres entrar?'
    if (raw.includes('Password should')) return 'la contraseña necesita al menos 6 caracteres'
    if (raw.includes('rate limit') || raw.includes('too many')) return 'demasiados intentos, espera un momento'
    return 'algo salió mal'
  }

  const now = new Date()
  const season = now.getMonth() >= 2 && now.getMonth() <= 4 ? 'primavera' :
                 now.getMonth() >= 5 && now.getMonth() <= 7 ? 'verano' :
                 now.getMonth() >= 8 && now.getMonth() <= 10 ? 'otoño' : 'invierno'
  const monthName = now.toLocaleDateString('es', { month: 'long' })

  return (
    <>
      <style>{CSS}</style>
      <div style={s.page}>
        <div style={s.bg} />

        {/* Climbing vine — top left */}
        <svg style={s.vineTL} viewBox="0 0 200 200" fill="none" stroke={theme.sage} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M40 200 C 40 150, 80 130, 70 90 C 62 55, 90 30, 120 20"/>
          <path d="M66 160 C 50 158, 40 150, 38 136 C 54 138, 64 146, 66 160 Z" fill={theme.sage} fillOpacity="0.35"/>
          <path d="M74 130 C 94 130, 104 120, 106 104 C 88 104, 78 114, 74 130 Z" fill={theme.sage} fillOpacity="0.35"/>
          <path d="M68 100 C 52 96, 44 84, 46 68 C 62 72, 70 84, 68 100 Z" fill={theme.sage} fillOpacity="0.35"/>
          <path d="M88 60 C 104 56, 114 44, 114 28 C 98 32, 90 44, 88 60 Z" fill={theme.sage} fillOpacity="0.35"/>
        </svg>

        {/* Sprout cluster — bottom right */}
        <svg style={s.vineBR} viewBox="0 0 200 200" fill="none" stroke={theme.terra} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 170 Q 100 160, 190 170" strokeWidth="2.2"/>
          <path d="M40 170 C 40 140, 50 120, 60 105"/>
          <path d="M60 130 C 40 128, 28 118, 28 102 C 44 104, 56 114, 60 130 Z" fill={theme.terra} fillOpacity="0.2"/>
          <path d="M60 110 C 78 108, 88 98, 88 82 C 74 84, 62 94, 60 110 Z" fill={theme.terra} fillOpacity="0.2"/>
          <path d="M100 170 C 100 130, 108 110, 116 92"/>
          <path d="M116 118 C 134 116, 144 106, 144 90 C 130 92, 118 102, 116 118 Z" fill={theme.terra} fillOpacity="0.2"/>
          <path d="M116 96 C 104 92, 98 82, 100 68 C 110 72, 116 82, 116 96 Z" fill={theme.terra} fillOpacity="0.2"/>
          <path d="M150 170 C 150 146, 156 130, 162 118"/>
          <path d="M162 136 C 174 134, 180 126, 180 114 C 170 116, 162 124, 162 136 Z" fill={theme.terra} fillOpacity="0.2"/>
        </svg>

        {/* Floating leaf sparks */}
        <LeafSpark style={{ ...s.spark, top:'14%', right:'16%', color: theme.sage,  width:14, height:14, animationDelay:'0s'   }} />
        <LeafSpark style={{ ...s.spark, top:'34%', left:'10%',  color: theme.gold,  width:11, height:11, animationDelay:'1.2s' }} />
        <LeafSpark style={{ ...s.spark, bottom:'28%', left:'22%', color: theme.terra, width:13, height:13, animationDelay:'2.4s' }} />
        <LeafSpark style={{ ...s.spark, top:'58%', right:'14%', color: '#4A7840',   width:12, height:12, animationDelay:'3.6s' }} />

        <div style={s.content}>
          {/* Brand */}
          <div style={s.brand}>
            <div style={s.mark} aria-label="planta que crece">
              <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width:54, height:54, color: theme.terraDeep, position:'relative', zIndex:1 }}>
                <path d="M10 50h44" stroke="#8A5A32" strokeWidth="2.2"/>
                <path d="M16 53h32" stroke="#8A5A32" strokeWidth="2.2" opacity="0.5"/>
                <path d="M32 50V20" stroke="#6A8A4A" strokeWidth="2" style={{ animation:'flGrow 3s ease-out forwards', transformOrigin:'32px 50px' }}/>
                <path d="M32 32c-6 0-10-3-10-9 7 0 10 3 10 9z" stroke="#6A8A4A" strokeWidth="1.8" fill="rgba(142,170,122,0.35)" style={{ animation:'flUnfurl 3s ease-out forwards', transformOrigin:'32px 32px' }}/>
                <path d="M32 26c5 0 8-2 8-7-5 0-8 2-8 7z" stroke="#6A8A4A" strokeWidth="1.8" fill="rgba(142,170,122,0.35)" style={{ animation:'flUnfurl 3s ease-out forwards', animationDelay:'0.4s', transformOrigin:'32px 32px' }}/>
                <path d="M32 20c0-3 2-5 4-6 0 4-2 6-4 6z" stroke={theme.terra} strokeWidth="1.6" fill="rgba(240,196,168,0.6)" style={{ animation:'flPop 3.4s ease-out forwards', transformOrigin:'32px 20px' }}/>
              </svg>
            </div>
            <h1 style={s.logo}>florece<span style={{ color: theme.terra, fontStyle:'italic', fontWeight:400 }}>.</span></h1>
            <p style={s.tagline}>tus finanzas, con alma</p>
            <span style={s.seasonBadge}>{season} · {monthName} {now.getFullYear()}</span>
          </div>

          {/* Welcome card */}
          <div style={s.welcome}>
            <div style={s.welcomeHi}>
              {modo === 'entrar'          && 'de vuelta al jardín ✦'}
              {modo === 'crear'           && 'empieza a florecer ✦'}
              {modo === 'recuperar'       && 'sin problema, pasa ✦'}
              {modo === 'nueva_contrasena' && 'elige tu nueva llave ✦'}
            </div>
            <div style={s.welcomeBody}>
              {modo === 'entrar' && <>como una planta que crece día a día, tu dinero también <em style={{ color: theme.ink, fontWeight:500 }}>echa raíces</em>. entra para seguir cultivando tus sueños.</>}
              {modo === 'crear' && <>cada gran jardín empieza con una <em style={{ color: theme.ink, fontWeight:500 }}>semilla</em>. crea tu cuenta y empieza a cultivar tu libertad financiera.</>}
              {modo === 'recuperar' && <>escribe tu correo y te enviamos un enlace para volver a entrar. sin juicios, sin preguntas — <em style={{ color: theme.ink, fontWeight:500 }}>pasa siempre</em>.</>}
              {modo === 'nueva_contrasena' && <>elige una contraseña que recuerdes fácil. algo que te haga sentir <em style={{ color: theme.ink, fontWeight:500 }}>en casa</em>.</>}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={s.form}>
            {modo !== 'nueva_contrasena' && (
              <div style={s.field}>
                <IcMail size={18} style={s.fieldIcon} />
                <div style={s.fieldInner}>
                  <label style={s.fieldLabel}>correo</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="hola@ejemplo.com" required style={s.fieldInput} />
                </div>
              </div>
            )}

            {(modo === 'entrar' || modo === 'crear') && (
              <div style={s.field}>
                <IcLock size={18} style={s.fieldIcon} />
                <div style={s.fieldInner}>
                  <label style={s.fieldLabel}>contraseña</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder={modo === 'crear' ? 'mínimo 6 caracteres' : '••••••••'}
                    required style={s.fieldInput} />
                </div>
              </div>
            )}

            {modo === 'nueva_contrasena' && (
              <>
                <div style={s.field}>
                  <IcLock size={18} style={s.fieldIcon} />
                  <div style={s.fieldInner}>
                    <label style={s.fieldLabel}>nueva contraseña</label>
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="mínimo 6 caracteres" required style={s.fieldInput} autoFocus />
                  </div>
                </div>
                <div style={s.field}>
                  <IcLock size={18} style={s.fieldIcon} />
                  <div style={s.fieldInner}>
                    <label style={s.fieldLabel}>confirmar contraseña</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="igual que arriba" required style={s.fieldInput} />
                  </div>
                </div>
              </>
            )}

            {modo === 'entrar' && (
              <div style={{ textAlign:'right', marginTop:-4 }}>
                <button type="button" style={s.forgotBtn}
                  onClick={() => { setModo('recuperar'); setError(''); setMensaje('') }}>
                  ¿olvidaste tu contraseña?
                </button>
              </div>
            )}

            {modo === 'recuperar' && (
              <div style={{ textAlign:'right', marginTop:-4 }}>
                <button type="button" style={s.forgotBtn}
                  onClick={() => { setModo('entrar'); setError(''); setMensaje('') }}>
                  volver a entrar
                </button>
              </div>
            )}

            {modo === 'crear' && (
              <>
                <div style={s.field}>
                  <IcUser size={18} style={s.fieldIcon} />
                  <div style={s.fieldInner}>
                    <label style={s.fieldLabel}>tu nombre</label>
                    <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
                      placeholder="¿cómo te llamas?" required style={s.fieldInput} />
                  </div>
                </div>
                <div style={s.generoWrap}>
                  <p style={s.generoLabel}>¿cómo te identifies?</p>
                  <div style={s.generoRow}>
                    {[{val:'femenino',label:'ella'},{val:'masculino',label:'él'},{val:'otro',label:'libre'}].map(({val,label}) => (
                      <button key={val} type="button" onClick={() => setGenero(val)}
                        style={{...s.generoPill, ...(genero===val ? s.generoPillActive : {})}}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={s.field}>
                  <div style={s.fieldInner}>
                    <label style={s.fieldLabel}>fecha de nacimiento (opcional)</label>
                    <input type="date" value={fechaNac} onChange={e => setFechaNac(e.target.value)}
                      style={{ ...s.fieldInput, paddingTop: 9 }} />
                  </div>
                </div>
              </>
            )}

            {error   && <p style={s.error}>{error}</p>}
            {mensaje && <p style={s.success}>{mensaje}</p>}

            <div style={s.ctaWrap}>
              <button type="submit" disabled={cargando} style={s.cta}
                onMouseEnter={() => setShineKey(k => k+1)}>
                <span key={shineKey} style={s.ctaShine} />
                {cargando       ? 'un momento...' :
                 modo === 'entrar'          ? 'entrar a mi jardín ✦' :
                 modo === 'crear'           ? 'crear mi cuenta ✦' :
                 modo === 'recuperar'       ? 'enviar enlace ✦' :
                                             'actualizar contraseña ✦'}
              </button>
            </div>

            {(modo === 'entrar' || modo === 'crear') && (
              <>
                <div style={s.divider}>
                  <span style={s.dividerLine}/><span style={s.dividerText}>o</span><span style={s.dividerLine}/>
                </div>
                <button type="button" style={s.signup}
                  onClick={() => { setModo(m => m === 'entrar' ? 'crear' : 'entrar'); setError(''); setMensaje('') }}>
                  {modo === 'entrar'
                    ? <>primera vez · <b style={{ color: theme.terra, fontWeight:600 }}>crear mi cuenta</b></>
                    : <>ya tengo cuenta · <b style={{ color: theme.terra, fontWeight:600 }}>entrar</b></>
                  }
                </button>
              </>
            )}
          </form>

          <div style={s.foot}>
            hecho con intención en méxico · <IcFlower size={12} style={{ verticalAlign:'-2px', color: theme.terra }} />
          </div>
        </div>
      </div>
    </>
  )
}

const makeStyles = (t) => ({
  page:           { minHeight: '100vh', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'stretch' },
  bg:             { position: 'absolute', inset: 0, zIndex: 0, background: `radial-gradient(ellipse 80% 60% at 30% 15%, rgba(238,216,152,0.5), transparent 60%), radial-gradient(ellipse 70% 50% at 80% 85%, rgba(240,196,168,0.4), transparent 60%), linear-gradient(170deg, ${t.cream}, ${t.paper} 60%, ${t.blush}44)` },
  vineTL:         { position: 'absolute', top: -20, left: -30, width: 200, height: 200, opacity: 0.18, pointerEvents: 'none', zIndex: 1 },
  vineBR:         { position: 'absolute', bottom: -40, right: -40, width: 220, height: 220, opacity: 0.14, pointerEvents: 'none', zIndex: 1, transform: 'scaleX(-1) rotate(8deg)' },
  spark:          { position: 'absolute', pointerEvents: 'none', zIndex: 1, animation: 'flDrift 6s ease-in-out infinite' },
  content:        { position: 'relative', zIndex: 2, width: '100%', maxWidth: 400, margin: '0 auto', padding: '60px 28px 32px', display: 'flex', flexDirection: 'column' },
  brand:          { textAlign: 'center', marginBottom: 0 },
  mark:           { width: 78, height: 78, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(circle at 50% 75%, #F0E4C8 0%, #E8D4A8 60%, #D8B888 100%)', borderRadius: '50%', boxShadow: `0 10px 26px ${t.accentDeep}52, inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -8px 18px ${t.accentDeep}26`, position: 'relative', overflow: 'hidden' },
  logo:           { fontFamily: fonts.display, fontSize: 54, fontWeight: 300, color: t.ink, margin: 0, letterSpacing: 4 },
  tagline:        { fontFamily: fonts.script, fontSize: 18, color: t.inkLight, margin: '8px 0 0' },
  seasonBadge:    { display: 'inline-block', marginTop: 12, fontFamily: fonts.body, fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: t.terra, padding: '5px 11px', borderRadius: 999, background: t.terra + '14', border: `1px solid ${t.terra}2E` },
  welcome:        { marginTop: 24, background: t.paper + '99', border: `1.5px solid ${t.cardBorder}`, borderRadius: 20, padding: '16px 18px', backdropFilter: 'blur(8px)', boxShadow: `0 6px 20px ${t.accentDeep}0D` },
  welcomeHi:      { fontFamily: fonts.script, fontSize: 18, color: t.terraDeep, marginBottom: 4 },
  welcomeBody:    { fontFamily: fonts.display, fontSize: 15, lineHeight: 1.45, color: t.inkMid },
  form:           { marginTop: 20, display: 'flex', flexDirection: 'column', gap: 0 },
  field:          { position: 'relative', background: t.paper + 'E6', border: `1.5px solid ${t.cardBorder}`, borderRadius: 14, padding: '4px 14px 4px 42px', marginBottom: 10, transition: 'all 0.2s', display: 'flex', alignItems: 'center' },
  fieldIcon:      { position: 'absolute', left: 14, color: t.inkLight, flexShrink: 0 },
  fieldInner:     { flex: 1 },
  fieldLabel:     { display: 'block', fontFamily: fonts.body, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, marginTop: 9 },
  fieldInput:     { width: '100%', border: 'none', background: 'transparent', fontFamily: fonts.body, fontSize: 14, fontWeight: 500, color: t.ink, padding: '3px 0 9px', outline: 'none', boxSizing: 'border-box' },
  forgotBtn:      { background: 'none', border: 'none', fontFamily: fonts.script, fontSize: 14, color: t.inkLight, cursor: 'pointer' },
  error:          { fontFamily: fonts.script, fontSize: 15, color: t.mauve, margin: '6px 0', textAlign: 'center' },
  success:        { fontFamily: fonts.script, fontSize: 15, color: t.sage, margin: '6px 0', textAlign: 'center' },
  ctaWrap:        { position: 'relative', marginTop: 14 },
  cta:            { position: 'relative', width: '100%', padding: '15px 20px', border: 'none', borderRadius: 18, background: `linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 45%), ${t.cardGradient}`, color: 'white', fontFamily: fonts.display, fontStyle: 'italic', fontSize: 18, fontWeight: 500, cursor: 'pointer', boxShadow: `0 14px 30px ${t.accentDeep}60, inset 0 1px 0 rgba(255,255,255,0.25)`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  ctaShine:       { position: 'absolute', top: 0, left: '-80%', width: '60%', height: '100%', background: 'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.35) 50%, transparent 80%)', transform: 'skewX(-20deg)', animation: 'flShine 0.7s ease forwards', pointerEvents: 'none' },
  divider:        { display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 12px', fontFamily: fonts.script, fontSize: 14, color: t.inkLight },
  dividerLine:    { flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${t.blush}66, transparent)` },
  dividerText:    {},
  signup:         { width: '100%', padding: 12, background: 'transparent', border: `1.5px solid ${t.cardBorder}`, borderRadius: 18, fontFamily: fonts.body, fontSize: 13, color: t.inkMid, cursor: 'pointer' },
  foot:           { marginTop: 'auto', paddingTop: 18, textAlign: 'center', fontFamily: fonts.script, fontSize: 13, color: t.inkLight, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 },
  generoWrap:     { marginBottom: 10 },
  generoLabel:    { fontFamily: fonts.body, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, marginBottom: 8, marginTop: 0 },
  generoRow:      { display: 'flex', gap: 8 },
  generoPill:     { flex: 1, padding: '10px 8px', borderRadius: 12, border: `1.5px solid ${t.cardBorder}`, background: t.paper + 'E6', fontFamily: fonts.body, fontSize: 13, color: t.inkMid, cursor: 'pointer' },
  generoPillActive: { background: t.accent + '1A', borderColor: t.terra, color: t.terraDeep, fontWeight: 600 },
})
